import { RefreshCcw } from 'lucide-react';
import { Badge, Button, Spinner } from '@helios/ui';
import { useAiStatus } from '@/hooks/useAiStatus';
import type { AiProviderStatus, AiRouteStatus } from '@/services/aiClient';
import {
  SettingsSection,
  SettingsTable,
  SettingsTableRow,
  SettingsTd,
} from '@/features/settings/settingsChrome';

const PROVIDER_LABELS: Record<string, string> = {
  anthropic: 'Anthropic Claude',
  openai: 'OpenAI',
};

const ROUTE_SOURCE_LABELS: Record<AiRouteStatus['source'], string> = {
  explicit: 'Explicit route',
  default: 'Default route',
  implicit: 'Configured providers',
};

/** Refresh displays safe metadata without invoking model inference or a connection probe. */
export function AiProvidersPanel() {
  const statusQuery = useAiStatus();
  const report = statusQuery.data;

  return (
    <SettingsSection
      title="Providers & routing"
      description="Claude handles business text. OpenAI supplies voice and images. Authentication checks and generation readiness are separate."
      actions={
        <Button
          type="button"
          variant="outline"
          size="xs"
          onClick={() => void statusQuery.refetch()}
          disabled={statusQuery.isFetching}
        >
          <RefreshCcw className="h-3.5 w-3.5" aria-hidden="true" />
          Refresh
        </Button>
      }
    >
      {statusQuery.isLoading ? (
        <div className="text-muted-foreground flex items-center gap-2 py-2 text-xs">
          <Spinner className="text-primary h-3.5 w-3.5" aria-hidden="true" />
          Loading provider status…
        </div>
      ) : statusQuery.isError || !report ? (
        <p className="border-border bg-background/60 text-muted-foreground rounded-md border px-2.5 py-1.5 text-xs">
          {statusQuery.error instanceof Error
            ? statusQuery.error.message
            : 'AI provider status is unavailable.'}
        </p>
      ) : (
        <div className="grid gap-2.5">
          <SettingsTable columns={['Provider', 'Status']} minWidth={420}>
            {report.providers.map((provider) => (
              <ProviderRow key={provider.key} provider={provider} />
            ))}
          </SettingsTable>
          <SettingsTable columns={['Task class', 'Execution', 'Source']} minWidth={520}>
            {report.routes.map((route) => (
              <SettingsTableRow key={route.taskClass}>
                <SettingsTd className="text-2xs font-mono">{route.taskClass}</SettingsTd>
                <SettingsTd className="text-muted-foreground">
                  <RouteExecution route={route} />
                </SettingsTd>
                <SettingsTd>
                  <Badge variant="outline" className="text-[10px]">
                    {route.taskClass === 'documents.extract'
                      ? 'Separate credentials'
                      : ROUTE_SOURCE_LABELS[route.source]}
                  </Badge>
                </SettingsTd>
              </SettingsTableRow>
            ))}
          </SettingsTable>
          <p className="text-2xs text-muted-foreground">
            Last 24h invocations —{' '}
            {['SUCCEEDED', 'DEGRADED', 'FAILED']
              .map((key) => `${key.toLowerCase()}: ${report.invocationsLast24h[key] ?? 0}`)
              .join(' · ')}
          </p>
        </div>
      )}
    </SettingsSection>
  );
}

function RouteExecution({ route }: { route: AiRouteStatus }) {
  if (route.taskClass === 'documents.extract' || route.executionPath === 'document_service') {
    return (
      <div>
        <p>Separate document service</p>
        <p className="text-2xs">
          {route.readiness === 'blocked'
            ? 'Blocked by policy'
            : route.readiness === 'unknown'
              ? 'Readiness unknown'
              : 'Readiness unavailable in this response'}
        </p>
        <p className="text-2xs">
          {route.detail ??
            'Service credentials and model are independent of the main provider list.'}
        </p>
      </div>
    );
  }
  return <>{route.chain.length > 0 ? route.chain.join(' → ') : 'No active provider route'}</>;
}

function ProviderRow({ provider }: { provider: AiProviderStatus }) {
  return (
    <SettingsTableRow>
      <SettingsTd>
        <p className="text-foreground font-medium">
          {PROVIDER_LABELS[provider.key] ?? provider.key}
        </p>
        {provider.model ? (
          <p className="text-muted-foreground text-2xs">
            {provider.model} · {provider.source ?? 'platform'}
          </p>
        ) : null}
        {provider.detail ? (
          <p className="text-2xs text-muted-foreground truncate">{provider.detail}</p>
        ) : null}
        <p className="text-2xs text-muted-foreground">
          Generation: not tested by authentication checks.
          {provider.checkedAt
            ? ` Authentication checked: ${provider.checkedAt}.`
            : ' Authentication has not been checked for this configuration.'}
        </p>
      </SettingsTd>
      <SettingsTd>
        {provider.configured ? (
          <Badge variant={provider.healthy ? 'success' : 'warning'}>
            {provider.verified
              ? 'Authentication accepted'
              : provider.checkedAt
                ? 'Authentication failed'
                : 'Credentials present · unchecked'}
          </Badge>
        ) : (
          <Badge variant="outline">
            {provider.source === 'disabled' ? 'Disabled for workspace' : 'Not configured'}
          </Badge>
        )}
      </SettingsTd>
    </SettingsTableRow>
  );
}
